import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash2, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useStore } from '../../context/StoreContext';
import { applyStorefrontTheme } from '../../shared/theme/storefrontTheme';
import Button from '../../shared/components/atoms/Button';
import QtyStepper from '../../shared/components/atoms/QtyStepper';
import logoPng from '../../assets/images/logo-png.png';
import {
  cartGroups,
  cartTotals,
  unitPrice,
  wasDiscounted,
  isProductUnavailable,
  stockCeiling,
  quantityPayload,
  money,
} from './cartModel';
import './cart.css';

/* ADDED TO YOUR CART — the drawer.
   Ported from SP-main's `paintCartPanel()` and the `.sf-ap*` rules.

   The prop contract is fixed: `isOpen` and `onClose`, called from the product
   page. The inside changed completely; the seam did not.

   It shows the whole cart where the reference shows one group: the reference
   panel is opened with the id of the product just added, and this component is
   handed no such id. The footer's "Cart subtotal" is then literally the cart's
   subtotal, and the parcel note still explains the split.

   ⚠ It renders in a portal on document.body and carries the theme itself: every
   storefront token is declared on `.sfx`, so outside the themed root
   `background: var(--wht)` resolves to nothing and the sheet renders
   see-through. The reference hit exactly this and fixed it the same way.

   ⚠ `applyStorefrontTheme` rewrites className, so `.sf-ap` and `.is-open` go on
   after it, imperatively. Moving them into JSX would let React overwrite the
   theme class on the next render. */

const CartView = ({ isOpen, onClose }) => {
  const {
    cartItems, addToCart, removeCartProduct, pageDetails,
  } = useStore();
  const navigate = useNavigate();

  /* Kept mounted through the close transition, then dropped — the panel is a
     fixed overlay and would otherwise sit over the page. */
  const [mounted, setMounted] = useState(isOpen);
  const scrimRef = useRef(null);
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  /* Focus goes back where it came from: otherwise closing the drawer drops focus
     onto <body> and a keyboard shopper restarts from the top of the document. */
  const openerRef = useRef(null);

  const appearance = pageDetails?.page_data?.colorAndApperances;

  useEffect(() => {
    if (isOpen) {
      openerRef.current = document.activeElement;
      setMounted(true);
      return undefined;
    }
    const t = setTimeout(() => setMounted(false), 300);
    return () => clearTimeout(t);
  }, [isOpen]);

  /* The theme, then the panel's own classes on top of it. */
  useEffect(() => {
    const el = scrimRef.current;
    if (!el) return;
    applyStorefrontTheme(el, appearance);
    el.classList.add('sf-ap');
    if (isOpen) {
      requestAnimationFrame(() => el.classList.add('is-open'));
    } else {
      el.classList.remove('is-open');
    }
  }, [appearance, mounted, isOpen]);

  const close = useCallback(() => {
    onClose();
    const opener = openerRef.current;
    if (opener && typeof opener.focus === 'function') {
      requestAnimationFrame(() => opener.focus());
    }
  }, [onClose]);

  /* Escape, and a focus trap. A dialog that lets Tab wander onto the page behind
     it is a dialog a screen reader user cannot tell the edges of. */
  useEffect(() => {
    if (!mounted || !isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mounted, isOpen, close]);

  useEffect(() => {
    if (mounted && isOpen) requestAnimationFrame(() => closeRef.current?.focus());
  }, [mounted, isOpen]);

  /* The page behind must not scroll under an open sheet. */
  useEffect(() => {
    if (!mounted || !isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [mounted, isOpen]);

  const setQuantity = (group, item, qty) => {
    const ceiling = stockCeiling(item);
    if (ceiling != null && qty > ceiling) {
      toast.error('Stock limit reached', { autoClose: 3000 });
      return;
    }
    addToCart(quantityPayload(group.raw, item, qty));
  };

  const removeLine = async (item) => {
    await removeCartProduct({ id: item.id });
  };

  if (!mounted) return null;

  const groups = cartGroups(cartItems);
  const totals = cartTotals(cartItems);

  const lineImage = (item) =>
    item?.product_variants?.image
    || item?.products?.product_images?.find((img) => img?.is_main === '1')?.src
    || item?.products?.product_images?.[0]?.src
    || logoPng;

  const body = groups.length === 0 ? (
    <div className="sf-apempty">
      <h3>Your cart is empty</h3>
      <p>Nothing in here yet.</p>
    </div>
  ) : (
    <>
      {groups.map((group) => (
        <div className="sf-apg" key={group.key} style={{ marginBottom: 14 }}>
          <div className="sf-apghead">
            <div>
              <span className="sf-apgname">Ships by {group.name}</span>
              {group.eta && <span className="sf-apgeta">{group.eta}</span>}
            </div>
            <span className={`sf-apgship${group.isFree ? ' is-free' : ''}`}>
              {group.shipNote}
            </span>
          </div>

          {group.items.map((item) => {
            const gone = isProductUnavailable(group.raw, item);
            const title = item?.products?.title || '';
            return (
              <div className="sf-apline" key={item.id}>
                <a
                  className="sf-apthumb"
                  href={`/product/${item?.products?.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    close();
                    navigate(`/product/${item?.products?.id}`);
                  }}
                >
                  <img src={lineImage(item)} alt="" loading="lazy" />
                </a>

                <div className="sf-apinfo">
                  <a
                    className="sf-apname"
                    href={`/product/${item?.products?.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      close();
                      navigate(`/product/${item?.products?.id}`);
                    }}
                  >
                    {title}
                  </a>
                  <div className="sf-apunit">{money(unitPrice(item))} each</div>
                </div>

                <QtyStepper
                  small
                  value={item.cart_quantity}
                  max={stockCeiling(item)}
                  disabled={gone}
                  itemLabel={title}
                  onChange={(qty) => setQuantity(group, item, qty)}
                  onDecrementAtMin={() => removeLine(item)}
                />

                {/* The struck original, where a brand discount is running. The
                    reference has no discount model, but ours is real, and a cart
                    that shows the saving on the cart page and hides it here is
                    two views of one cart disagreeing. */}
                <div className="sf-apprice">
                  {wasDiscounted(item) && (
                    <span className="ct-lwas">
                      {money(parseFloat(item.product_variants.msrp_price) * item.cart_quantity)}
                    </span>
                  )}
                  {money(unitPrice(item) * item.cart_quantity)}
                </div>

                <button
                  className="ct-rm"
                  type="button"
                  onClick={() => removeLine(item)}
                  aria-label={`Remove ${title}`}
                >
                  <Trash2 aria-hidden="true" />
                </button>

                {gone && (
                  <p className="ct-gone">
                    This is no longer available — remove it to check out.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* Only when there is a split to explain: one parcel needs no sentence
          about parcels. */}
      {totals.parcels > 1 && (
        <p className="sf-apmore">
          <Package aria-hidden="true" />
          <span>
            Your cart holds {totals.parcels} brands, so this order arrives
            in {totals.parcels} parcels.
          </span>
        </p>
      )}
    </>
  );

  return createPortal(
    <div ref={scrimRef} onClick={(e) => { if (e.target === scrimRef.current) close(); }}>
      <aside
        className="sf-apin"
        role="dialog"
        aria-modal="true"
        aria-label="Added to your cart"
        ref={panelRef}
      >
        <header className="sf-aphead">
          <span className="sf-aptitle">Added to your cart</span>
          <button
            className="sf-apx"
            type="button"
            onClick={close}
            aria-label="Close"
            ref={closeRef}
          >
            <X aria-hidden="true" />
          </button>
        </header>

        <div className="sf-apbody">{body}</div>

        {groups.length > 0 && (
          <footer className="sf-apfoot">
            <div className="ct-srow">
              <span>Cart subtotal</span>
              <span>{money(totals.subtotal)}</span>
            </div>
            {/* Shipping and tax are not summed here, deliberately: the cart is
                where the parcels and their terms are laid out, and a second total
                in a panel is a second chance to disagree with it. */}
            <p className="sf-apnote">Shipping and estimated tax are shown in your cart.</p>
            <Button
              variant="primary"
              className="sf-apgo"
              onClick={() => { close(); navigate('/viewcart'); }}
            >
              Go to cart
            </Button>
            <Button variant="ghost" className="sf-apkeep" onClick={close}>
              Keep shopping
            </Button>
          </footer>
        )}
      </aside>
    </div>,
    document.body,
  );
};

export default CartView;
